import { useState } from 'react'
import { PRODUCT_ENTITLEMENTS } from '@headline/schemas'
import { useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router'
import { z } from 'zod'
import { PageHeader } from '@/components/PageHeader'
import { IssueActions } from '@/components/issues/IssueActions'
import { AssignedIA } from '@/components/reports/AssignedIA'
import { NewReportDialog } from '@/components/reports/NewReportDialog'
import {
  ReportConfigHeader,
  ReportNameLink,
  StopRowClick,
} from '@/components/reports/ReportNameLink'
import { configRowProps } from '@/components/reports/config-row'
import { SendModeBadge } from '@/components/reports/SendModeBadge'
import { ReportStatusBar } from '@/components/reports/ReportStatusBar'
import { UnpauseReportButton } from '@/components/reports/UnpauseReportButton'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { accountQuery, reportsOverviewQuery } from '@/lib/queries'
import { DEFAULT_STATUS_FILTER, reportStatusFilterSchema, splitByStatus } from '@/lib/status-filter'
import { tzLabel } from '@/lib/timezones'
import { useCanEditAccount } from '@/lib/use-account-permissions'

/**
 * Reports for one account (the URL place), across its clients. A report binds to a client, so
 * creating picks the client (the account is fixed here) then opens the new report's config.
 */
export const Route = createFileRoute('/app/accounts/$accountId_/reports/')({
  validateSearch: z.object({ status: reportStatusFilterSchema }),
  loader: async ({ context, params }) => {
    // Reports is a product: an account that didn't buy it has no Reports surface. Redirect to the
    // account Home rather than render an empty page. Matches the sidebar, which hides the entry.
    const account = await context.queryClient.ensureQueryData(accountQuery(params.accountId))
    if (!account.entitlements.includes(PRODUCT_ENTITLEMENTS.reports)) {
      throw redirect({ to: '/app/accounts/$accountId', params: { accountId: params.accountId } })
    }
    await context.queryClient.ensureQueryData(reportsOverviewQuery)
  },
  component: AccountReportsPage,
})

function AccountReportsPage() {
  const navigate = useNavigate()
  const { accountId } = Route.useParams()
  const status = Route.useSearch().status ?? DEFAULT_STATUS_FILTER
  const { data: account } = useSuspenseQuery(accountQuery(accountId))
  const { data: overview } = useSuspenseQuery(reportsOverviewQuery)
  const [creating, setCreating] = useState(false)
  // Creating a report writes to a sub-account, which an account:edit grant cascades into. A
  // view-only user sees the list but not the button — the API would 403 the create anyway.
  const canCreate = useCanEditAccount(accountId)

  const { shown, counts } = splitByStatus(
    overview.reports.filter((report) => report.accountId === accountId),
    status,
  )
  const setStatus = (next: typeof status) =>
    void navigate({ to: '.', search: { status: next }, replace: true })

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <PageHeader
          title="Reports"
          description={`${account.name}'s reports. Configure delivery, coverage and headers from a row; presentation lives with the account's template.`}
        />
        {canCreate && <Button onClick={() => setCreating(true)}>New report</Button>}
      </div>
      <ReportStatusBar
        status={status}
        counts={counts}
        isEmpty={shown.length === 0}
        emptyLabel={
          canCreate
            ? `No reports for ${account.name} yet. Create one with the button above.`
            : `No reports for ${account.name} yet.`
        }
        onChange={setStatus}
      />
      {shown.length > 0 && (
        <div className="overflow-x-auto rounded-md border border-border">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                <th className="px-3 py-2 text-left font-medium">
                  <ReportConfigHeader />
                </th>
                <th className="px-3 py-2 text-left font-medium">Client</th>
                <th className="px-3 py-2 text-left font-medium">IA</th>
                <th className="px-3 py-2 text-left font-medium">Status</th>
                <th className="px-3 py-2 text-left font-medium">Mode</th>
                <th className="px-3 py-2 text-left font-medium">Send time</th>
                {/* Centred, because its contents are two buttons rather than a value — a
                    left-aligned label over centred controls reads as a mistake. */}
                <th className="px-3 py-2 text-center font-medium">Drafts</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((report) => (
                <tr
                  key={report.id}
                  {...configRowProps(
                    () =>
                      void navigate({
                        to: '/app/accounts/$accountId/reports/$reportId',
                        params: { accountId, reportId: report.id },
                      }),
                  )}
                >
                  <td className="px-3 py-2">
                    <ReportNameLink accountId={accountId} reportId={report.id} name={report.name} />
                  </td>
                  <td className="px-3 py-2 text-muted-foreground">{report.subaccountName}</td>
                  <td className="px-3 py-2">
                    <AssignedIA owners={report.owners} />
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-2">
                      <Badge variant={report.status === 'active' ? 'default' : 'secondary'}>
                        {report.status}
                      </Badge>
                      <UnpauseReportButton report={report} canEdit={canCreate} />
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <SendModeBadge mode={report.sendMode} />
                  </td>
                  <td className="px-3 py-2 tabular-nums">
                    {report.sendTimes.join(', ')} {tzLabel(report.timezone)}
                    <span className="text-xs text-muted-foreground"> {report.schedule}</span>
                  </td>
                  <td className="px-3 py-1.5">
                    <StopRowClick>
                      <div className="flex justify-center">
                        <IssueActions report={report} />
                      </div>
                    </StopRowClick>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {creating && (
        <NewReportDialog
          accounts={[{ id: accountId, name: account.name }]}
          onClose={() => setCreating(false)}
        />
      )}
    </div>
  )
}
